import { useState } from 'react';
import { PatientEnroller } from './usePatientEnrollers';

export interface PADashboardState {
  // Search state
  searchTerm: string;
  isSearching: boolean;
  
  // Pagination state
  currentPage: number;
  recordsPerPage: number;
  
  // Dialog states
  showLogoutDialog: boolean;
  showEditDialog: boolean;
  showDeleteDialog: boolean;
  showAddDialog: boolean;
  
  // Current view state
  currentView: string;
  
  // Selected items
  editingEnroller: PatientEnroller | null;
  deletingEnroller: PatientEnroller | null;
  
  // Loading states
  isRefreshing: boolean;
}

export interface PADashboardActions {
  // Search actions
  setSearchTerm: (term: string) => void;
  setIsSearching: (searching: boolean) => void;
  
  // Pagination actions
  setCurrentPage: (page: number) => void;
  setRecordsPerPage: (records: number) => void;
  
  // Dialog actions
  setShowLogoutDialog: (show: boolean) => void;
  setShowEditDialog: (show: boolean) => void;
  setShowDeleteDialog: (show: boolean) => void;
  setShowAddDialog: (show: boolean) => void;
  
  // View actions
  setCurrentView: (view: string) => void;
  
  // Selection actions
  setEditingEnroller: (enroller: PatientEnroller | null) => void;
  setDeletingEnroller: (enroller: PatientEnroller | null) => void;
  
  // Loading actions
  setIsRefreshing: (refreshing: boolean) => void;
}

export const usePADashboardState = (patientEnrollers: PatientEnroller[]) => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  
  // Dialog states
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Current view state
  const [currentView, setCurrentView] = useState('dashboard');
  
  // Selected items
  const [editingEnroller, setEditingEnroller] = useState<PatientEnroller | null>(null);
  const [deletingEnroller, setDeletingEnroller] = useState<PatientEnroller | null>(null);
  
  // Loading states
  const [isRefreshing, setIsRefreshing] = useState(false);

  const state: PADashboardState = {
    searchTerm,
    isSearching,
    currentPage,
    recordsPerPage,
    showLogoutDialog,
    showEditDialog,
    showDeleteDialog,
    showAddDialog,
    currentView,
    editingEnroller,
    deletingEnroller,
    isRefreshing,
  };

  const actions: PADashboardActions = {
    setSearchTerm,
    setIsSearching,
    setCurrentPage,
    setRecordsPerPage,
    setShowLogoutDialog,
    setShowEditDialog,
    setShowDeleteDialog,
    setShowAddDialog,
    setCurrentView,
    setEditingEnroller,
    setDeletingEnroller,
    setIsRefreshing,
  };

  return { state, actions };
};